import { useState } from 'react';
import { useTheme } from '../App';

const C  = '#00E5FF';
const LC = '#00BDCC';

export default function MaintenancePage() {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const ac = isLight ? LC : C;

  const [phone, setPhone]     = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [focused, setFocused] = useState(false);

  const handleNotify = () => {
    const trimmed = phone.trim();
    if (!trimmed) return;
    window.open(`https://wa.me/918884108992?text=${encodeURIComponent("Hey, notify me when Propspector launches!")}`, "_blank");
    setSubmitted(true);
  };

  const handleKey = e => {
    if (e.key === 'Enter') handleNotify();
  };

  return (
    <div style={{
      minHeight: '100vh', width: '100%',
      background: isLight ? '#F5F3EF' : '#080d16',
      color: isLight ? '#0F0E0C' : '#f1f5f9',
      fontFamily: "'Manrope', sans-serif",
      display: 'flex', flexDirection: 'column',
      position: 'relative', overflow: 'hidden',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Manrope:wght@400;500;600;700&display=swap');

        @keyframes mpGlow {
          0%, 100% { box-shadow: 0 0 18px ${ac}30; }
          50%       { box-shadow: 0 0 36px ${ac}55; }
        }
        @keyframes mpFadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .mp-fade-1 { animation: mpFadeUp 0.55s ease-out 0.05s both; }
        .mp-fade-2 { animation: mpFadeUp 0.55s ease-out 0.15s both; }
        .mp-fade-3 { animation: mpFadeUp 0.55s ease-out 0.28s both; }
        .mp-fade-4 { animation: mpFadeUp 0.55s ease-out 0.42s both; }
        .mp-fade-5 { animation: mpFadeUp 0.55s ease-out 0.56s both; }
      `}</style>

      {/* Radial glow backdrop (dark only) */}
      {!isLight && (
        <div style={{
          position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(ellipse 60% 45% at 50% 42%, rgba(0,229,255,0.045) 0%, transparent 70%)',
        }} />
      )}

      {/* ── Nav bar ── */}
      <nav style={{
        position: 'relative', zIndex: 10,
        height: 58,
        display: 'flex', alignItems: 'center',
        padding: '0 28px',
        borderBottom: isLight
          ? '1px solid rgba(0,0,0,0.07)'
          : '1px solid rgba(255,255,255,0.05)',
        background: isLight
          ? 'rgba(245,243,239,0.92)'
          : 'rgba(8,13,22,0.88)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{
            width: 28, height: 28,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <img src="/logo.png" alt="Propspector logo" style={{ width: 26, height: 26, objectFit: 'contain', display: 'block' }} />
          </div>
          <span style={{
            fontFamily: "'Sora', sans-serif",
            fontWeight: 700, fontSize: '0.95rem',
            letterSpacing: '-0.01em',
            color: isLight ? '#0F0E0C' : '#ffffff',
          }}>
            Propspector
          </span>
        </div>
      </nav>

      {/* ── Main content ── */}
      <main style={{
        position: 'relative', zIndex: 1,
        flex: 1,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '40px 24px 80px',
        textAlign: 'center',
      }}>

        {/* Status pill */}
        <div className="mp-fade-1" style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          background: isLight ? 'rgba(0,189,204,0.08)' : 'rgba(0,229,255,0.07)',
          border: `1px solid ${isLight ? 'rgba(0,189,204,0.28)' : 'rgba(0,229,255,0.22)'}`,
          borderRadius: 100, padding: '5px 16px', marginBottom: 24,
        }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: ac,
            boxShadow: `0 0 6px ${ac}`,
            animation: 'mpGlow 2s ease-in-out infinite',
          }} />
          <span style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: '0.68rem', fontWeight: 700,
            color: ac, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>
            Coming Soon
          </span>
        </div>

        {/* Headline */}
        <h1 className="mp-fade-2" style={{
          fontFamily: "'Sora', sans-serif",
          fontWeight: 800,
          fontSize: 'clamp(1.6rem, 5vw, 2.8rem)',
          letterSpacing: '-0.03em',
          color: isLight ? '#0F0E0C' : '#f1f5f9',
          margin: '0 0 16px',
          lineHeight: 1.15,
          maxWidth: 560,
        }}>
          Know your property before you own it
        </h1>

        {/* Subtext */}
        <p className="mp-fade-3" style={{
          fontFamily: "'Manrope', sans-serif",
          fontSize: '0.92rem', fontWeight: 400,
          color: isLight ? '#6A6560' : '#94a3b8',
          lineHeight: 1.65,
          maxWidth: 420, margin: '0 0 44px',
        }}>
          Bangalore&rsquo;s first AI-powered property inspection service.
          We check what sellers won&rsquo;t show you — so you buy with proof, not promises.
        </p>

        <p className="mp-fade-4" style={{
          fontFamily: "'Manrope', sans-serif",
          fontStyle: 'italic',
          fontSize: '0.76rem', fontWeight: 400,
          color: isLight ? '#9A9490' : '#64748b',
          margin: '-28px 0 44px',
          maxWidth: 420,
        }}>
          Strike the truth before you strike the deal
        </p>

        {/* WhatsApp capture */}
        <div className="mp-fade-5" style={{ width: '100%', maxWidth: 420 }}>
          {submitted ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              background: isLight ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.3)',
              borderRadius: 50, padding: '14px 24px',
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: '0.86rem', fontWeight: 600, color: '#10b981',
              }}>
                You&rsquo;re on the list — we&rsquo;ll be in touch!
              </span>
            </div>
          ) : (
            <div style={{
              display: 'flex', gap: 8,
              flexWrap: 'wrap', justifyContent: 'center',
            }}>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                onKeyDown={handleKey}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="Your WhatsApp number"
                style={{
                  flex: '1 1 220px', minWidth: 0,
                  background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
                  border: focused
                    ? `1.5px solid ${ac}`
                    : isLight
                      ? '1.5px solid rgba(0,0,0,0.14)'
                      : '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 50,
                  padding: '13px 22px',
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: '0.88rem',
                  color: isLight ? '#0F0E0C' : '#f1f5f9',
                  outline: 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  boxShadow: focused ? `0 0 0 3px ${ac}18` : 'none',
                }}
              />
              <button
                onClick={handleNotify}
                disabled={!phone.trim()}
                style={{
                  flexShrink: 0,
                  background: phone.trim()
                    ? (isLight ? '#00BDCC' : C)
                    : isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.07)',
                  color: phone.trim()
                    ? '#ffffff'
                    : isLight ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.25)',
                  border: 'none',
                  borderRadius: 50,
                  padding: '13px 26px',
                  fontFamily: "'Sora', sans-serif",
                  fontWeight: 600, fontSize: '0.86rem',
                  cursor: phone.trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                  boxShadow: phone.trim()
                    ? (isLight ? '0 4px 18px rgba(0,189,204,0.28)' : `0 0 18px ${C}35`)
                    : 'none',
                }}
              >
                Notify me on WhatsApp
              </button>
            </div>
          )}

          <p style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: '0.65rem',
            color: isLight ? '#9A9490' : '#475569',
            marginTop: 12, marginBottom: 0,
          }}>
            We&rsquo;ll message you when we go live.
          </p>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer style={{
        position: 'relative', zIndex: 1,
        textAlign: 'center', padding: '0 24px 28px',
      }}>
        <span style={{
          fontFamily: "'Manrope', sans-serif",
          fontSize: '0.65rem',
          color: isLight ? '#C0BBB5' : '#334155',
        }}>
          propspector.in
        </span>
      </footer>
    </div>
  );
}
